// packages/web/src/components/marketplace/ServiceGrid.tsx
// Service display grid component

import {
  Grid, Box, Typography, Button, CircularProgress
} from '@mui/material';
import React from 'react';

import { ServiceCardNew as ServiceCard } from '@/components/marketplace/ServiceCardNew';
import type { UserAddress } from '@/lib/api/addresses';
import type { MarketplaceService } from '@/types/pricing';

interface ServiceGridProps {
  services: MarketplaceService[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  loadingMore: boolean;
  selectedAddress: UserAddress | null;
  onServiceClick: (service: MarketplaceService) => void;
}

/**
 * Component for displaying services in a responsive grid
 */
export function ServiceGrid({
  services,
  loading,
  error,
  hasMore,
  onLoadMore,
  loadingMore,
  selectedAddress,
  onServiceClick
}: ServiceGridProps) {
  // Helper function to determine service availability
  const getServiceAvailability = (
    service: any,
    userAddress: UserAddress | null
  ): {
    available: boolean;
    reason: string;
    requiresQuote?: boolean;
  } => {
    // Use geographic restrictions instead of old service types
    const restrictions = service.geographic_restrictions;

    if (!restrictions || restrictions.type === 'everywhere') {
      return {
        available: true,
        reason: 'Available everywhere',
        requiresQuote: false
      };
    }

    // Service has geographic restrictions - check if user has address for verification
    if (restrictions.requires_address_verification && !userAddress) {
      return {
        available: false,
        reason: 'Address verification required',
        requiresQuote: false
      };
    }

    // Service is available (passed server-side geographic filtering)
    return {
      available: true,
      reason: `Available in ${restrictions.locations?.length || 0} locations`,
      requiresQuote: restrictions.requires_address_verification
    };
  };

  if (error) {
    return (
      <Typography color="error" sx={{ mb: 2 }} data-testid="service-grid-error">
        {error}
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py={8} data-testid="service-grid-loading">
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Grid container spacing={2} data-testid="service-grid">
      {services.map((service, index) => {
        const key = service.id || `service-${index}`;
        const availability = getServiceAvailability(service as any, selectedAddress);
        const isBuyBoxWinner = !!(service as any).buyBoxWinner;

        // Debug winning perk data
        if ((service as any).winningPerk) {
          console.log('[SERVICEGRID] Service with winning perk:', {
            id: service.id,
            name: service.name,
            winningPerk: (service as any).winningPerk
          });
        }

        console.log('[SERVICEGRID] Rendering service card:', {
          id: service.id,
          name: service.name,
          hasWinningPerk: !!(service as any).winningPerk,
          winningPerk: (service as any).winningPerk
        });

        return (
          <Grid
            item
            xs={12}
            sm={12}
            md={6}
            lg={4}
            key={key}
          >
            <ServiceCard
              service={service}
              onCardClick={onServiceClick}
            />
          </Grid>
        );
      })}

      {!services.length && (
        <Grid item xs={12}>
          <Box py={8} textAlign="center">
            <Typography color="text.secondary">
              No services found. Try adjusting your filters or search terms.
            </Typography>
          </Box>
        </Grid>
      )}

      {/* Load More Button */}
      {hasMore && services.length > 0 && (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" py={4}>
            <Button
              variant="outlined"
              onClick={onLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={16} /> : null}
            >
              {loadingMore ? 'Loading...' : 'Load More Services'}
            </Button>
          </Box>
        </Grid>
      )}
    </Grid>
  );
}